<script setup lang="ts">
import type { HTMLAttributes } from "vue"
import { computed, ref, watch } from "vue"
import { Select } from "@/shared/ui/select"

const props = withDefaults(
  defineProps<{
    modelValue: number
    min?: number
    max?: number
    step?: number
    disabled?: boolean
    invalid?: boolean
    id?: string
    ariaLabel?: string
    ariaLabelledby?: string
    class?: HTMLAttributes["class"]
  }>(),
  {
    min: 0,
    max: 1440,
    step: 15,
  },
)

const emit = defineEmits<{
  "update:modelValue": [value: number]
}>()

function formatMinutes(minutes: number): string {
  if (minutes === 1440) return "24:00"
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
}

const options = computed(() => {
  const result: { value: string; label: string }[] = []
  for (let m = props.min; m <= props.max; m += props.step) {
    result.push({ value: String(m), label: formatMinutes(m) })
  }
  return result
})

const localString = ref(String(props.modelValue))

watch(
  () => props.modelValue,
  (v) => {
    localString.value = String(v)
  },
)

function onUpdate(val: string | null) {
  if (val !== null) {
    localString.value = val
    emit("update:modelValue", Number(val))
  }
}
</script>

<template>
  <Select
    :model-value="localString"
    :options="options"
    :disabled="disabled"
    :invalid="invalid"
    :id="id"
    :aria-label="ariaLabel"
    :aria-labelledby="ariaLabelledby"
    :class="props.class"
    @update:model-value="onUpdate"
  />
</template>
